// Workbench materials gate — pure helper that mirrors the server-side
// `inputAvailable` check (see player-workshop.ts) so the queue button can
// disable before round-tripping. Craftable inputs (e.g. copper_wire) match
// any quality-suffixed variant in inventory; non-craftable raw inputs match
// by exact id.
//
// Phase 7 Block D — workbench materials column. Server is still
// authoritative; this only powers the UI gate.

import { isCraftableBaseId } from "../../shared/items.ts";
import { parseItemId } from "../../shared/quality.ts";
import type { Recipe } from "../../shared/recipes.ts";
import type { InventorySlot } from "../../shared/messages.ts";

export type MaterialAssessment = {
  itemId: string;
  need: number;
  have: number;
  sufficient: boolean;
};

export type MaterialsResult = {
  sufficient: boolean;
  perInput: MaterialAssessment[];
};

// Sums all inventory counts that satisfy `inputId`. For craftable base
// ids, every quality-suffixed variant counts (matches server behavior).
export function countInInventory(inventory: readonly InventorySlot[], inputId: string): number {
  if (isCraftableBaseId(inputId)) {
    let total = 0;
    for (const slot of inventory) {
      const { baseId } = parseItemId(slot.itemId);
      if (baseId === inputId) total += slot.count;
    }
    return total;
  }
  let total = 0;
  for (const slot of inventory) if (slot.itemId === inputId) total += slot.count;
  return total;
}

// Returns per-input have/need + an overall sufficient flag. Used by the
// info pane to render red/green rows and by the action bar to gate the
// Queue Craft button.
export function assessMaterials(
  recipe: Recipe,
  inventory: readonly InventorySlot[],
  batchCount: number,
): MaterialsResult {
  const perInput: MaterialAssessment[] = recipe.inputs.map((inp) => {
    const need = inp.count * batchCount;
    const have = countInInventory(inventory, inp.itemId);
    return { itemId: inp.itemId, need, have, sufficient: have >= need };
  });
  return {
    sufficient: perInput.every((p) => p.sufficient),
    perInput,
  };
}
